/**
 * Client-side duel helpers (Player duels, PRD #252, Slice C / ADR-0130).
 *
 * The **pure** pieces of the challenger's front-of-flow: the chat triggers that
 * arm the duel target cursor - the spoken "I wish to duel" phrase and its `/duel`
 * slash-command alias, unified behind the `classifyDuelChat` router (ADR-0165) -
 * and the blue-hover eligibility test that decides whether a hovered mobile is a
 * plausible target. All are extracted here, free of the DOM and the renderer, so
 * they are node-testable in isolation (the PRD's "phrase match and blue-hover
 * selection node-tested where pure"); the glue that wires them to chat input, the
 * cursor, and the hover highlight lives in `main.ts` and `gameRenderer.ts`.
 *
 * The client is deliberately not the authority: this test is a best-effort UX
 * filter (it cannot tell a player from an NPC - neither client entity carries a
 * marker), so it errs toward *showing* the blue highlight, and the server's
 * `checkDuelTarget` makes the real ruling. An invalid pick the client let through
 * fizzles silently server-side.
 */
import { DUEL_CHALLENGE_RANGE, DUEL_SWING_RANGE, DUEL_BOW_RANGE, chebyshev } from './sim/index';

/**
 * The exact spoken call-out that arms the duel cursor, normalized (trimmed +
 * lower-cased) for the match. Kept as a constant so the trigger has one source of
 * truth the test pins.
 */
export const DUEL_PHRASE = 'i wish to duel';

/**
 * The `/duel` slash command - a discoverable alias for {@link DUEL_PHRASE} that arms
 * the exact same duel target cursor (ADR-0165). Normalized (trimmed + lower-cased)
 * like the phrase so the trigger has one source of truth the test pins.
 */
export const DUEL_COMMAND = '/duel';

/**
 * Whether a line the player just spoke is the duel call-out (PRD story 1). Matched
 * on the player's **own outgoing** speech, case-insensitively and trimmed; a match
 * arms the target cursor client-side, but the caller still broadcasts the line as
 * ordinary speech, so the whole room hears "I wish to duel". Only the exact phrase
 * arms it - a line that merely contains it does not, so ordinary chat quoting the
 * words never traps the speaker into targeting mode.
 */
export function matchesDuelPhrase(text: string): boolean {
  return text.trim().toLowerCase() === DUEL_PHRASE;
}

/**
 * Whether a line is the `/duel` slash-command alias (ADR-0165). Routes to the **same**
 * duel-initiation path as {@link matchesDuelPhrase} - both arm the duel target cursor
 * via `armDuelTargeting` - but, being a client-side slash command like `/dress`, the
 * caller early-returns instead of broadcasting the line as speech (the room does not
 * hear the raw `/duel`). Matched on the exact command, trimmed + case-insensitively,
 * so ordinary chat mentioning it never traps the speaker into targeting mode.
 */
export function matchesDuelCommand(text: string): boolean {
  return text.trim().toLowerCase() === DUEL_COMMAND;
}

/** How the chat-input router should treat a just-typed line for duel purposes. */
export interface DuelChatIntent {
  /** Arm the duel target cursor (`armDuelTargeting`). */
  arm: boolean;
  /**
   * The line is a client-side slash command and must **not** broadcast as speech.
   * True only for the `/duel` alias; the spoken phrase falls through to the room.
   */
  suppress: boolean;
}

/**
 * The single classifier both the spoken "I wish to duel" phrase and the `/duel`
 * slash-command alias route through (ADR-0165), so the two entry points can never
 * drift apart: **both** set `arm: true` (the one `armDuelTargeting` duel-initiation
 * path), and only `/duel` sets `suppress: true` (a slash command, like `/dress`, is
 * consumed client-side rather than echoed as chat). Any other line is inert.
 */
export function classifyDuelChat(text: string): DuelChatIntent {
  if (matchesDuelCommand(text)) return { arm: true, suppress: true };
  if (matchesDuelPhrase(text)) return { arm: true, suppress: false };
  return { arm: false, suppress: false };
}

/** What the blue-hover test needs about a hovered mobile, read from the read-model. */
export interface DuelHoverContext {
  /** The hovered entity is the local player's own sprite. */
  isSelf: boolean;
  /** The hovered entity is a giant rat (the client knows this by its body id). */
  isRat: boolean;
  /** Chebyshev tiles between the challenger and the hovered mobile. */
  distance: number;
}

/**
 * Whether a hovered mobile should highlight blue as a duel-target candidate
 * (PRD story 3). A best-effort client filter: it excludes the obvious non-targets
 * the client *can* see - the challenger themselves, a rat, and anything beyond
 * {@link DUEL_CHALLENGE_RANGE} - and shows the highlight for everything else,
 * leaving the authoritative ruling (NPC / ghost / busy) to the server. Erring
 * toward showing the highlight keeps the feedback responsive; a pick the server
 * then refuses simply fizzles.
 */
export function isDuelTargetCandidate(ctx: DuelHoverContext): boolean {
  return !ctx.isSelf && !ctx.isRat && ctx.distance <= DUEL_CHALLENGE_RANGE;
}

/**
 * Chebyshev tile distance between two points - re-exported from the sim so the
 * hover glue in `main.ts` shares the one metric the range check uses rather than
 * re-deriving it.
 */
export { chebyshev };

/**
 * The duel punch-range and bow-range thresholds (Fire your bow!, ADR-0255),
 * re-exported from the sim so `main.ts` shows the "Press F to auto-fire your bow!" prompt
 * against the exact same tile bounds the server's `fireDuelBow` enforces: the prompt
 * appears while the opponent is beyond {@link DUEL_SWING_RANGE} (out of punch reach)
 * but within {@link DUEL_BOW_RANGE} (a shot can still land).
 */
export { DUEL_SWING_RANGE, DUEL_BOW_RANGE };
